/**
 * Browser-safe random identifier for client-generated tokens: idempotency
 * keys, optimistic message ids, and similar. These are not entity ids — those
 * come from `coreIds.generate` on the server.
 *
 * Prefers `crypto.randomUUID()` and falls back to `crypto.getRandomValues`
 * (available everywhere `randomUUID` is not, e.g. non-secure contexts). Throws
 * when Web Crypto is missing entirely rather than degrading to `Math.random`,
 * so callers never silently get a weak id.
 */
export function randomClientId(): string {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }
  if (typeof webCrypto?.getRandomValues !== 'function') {
    throw new Error('Web Crypto is required to generate a client id');
  }
  const values = new Uint32Array(4);
  webCrypto.getRandomValues(values);
  return Array.from(values, (value) => value.toString(16).padStart(8, '0')).join('-');
}
